import { useContext, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { BoxForm, SelectAutocomplete, StackForm, Switch } from 'components/Form'
import Loading from 'components/Loading'
import { SelectOption } from 'components/Select'
import { AbTest } from 'features/ab-tests/types/abTest'
import { AmazonPageContext } from 'features/globalSetup/features/amazon/contexts/AmazonPageContext'
import { useAbility } from 'providers/casl'
import { DomainAmazonOption, useDomainAmazonData } from '../../hooks/useDomainAmazonData'
import { Schema } from '../../schema'
import { optionsMapper } from './utils'

export const DomainSection = () => {
  const ability = useAbility()
  const { t } = useTranslation('features/globalSetup/amazon', {
    keyPrefix: 'pages.inventoryConnection.basicAttributes',
  })
  const { setActiveAbTestId } = useContext(AmazonPageContext)
  const { control, setValue, watch } = useFormContext<Schema>()
  const { id } = watch()

  const { loading, options } = useDomainAmazonData()

  useEffect(() => {
    const activeAbTestId: AbTest['id'] | undefined = options.find(
      (option: DomainAmazonOption): boolean => option.value === id.value,
    )?.activeAbTestId

    if (setActiveAbTestId) {
      setActiveAbTestId(activeAbTestId)
    }
  }, [id])

  if (loading) {
    return <Loading />
  }

  const isDisabled: boolean = ability.cannot('update', 'GlobalSetupFeature-amazon')

  return (
    <BoxForm>
      <StackForm>
        <SelectAutocomplete<Schema, DomainAmazonOption>
          control={control}
          label={t('id.label')}
          name='id'
          onChange={(selectedOption: SelectOption<DomainAmazonOption>): void =>
            setValue('id', selectedOption, { shouldDirty: false })
          }
          options={optionsMapper(options)}
        />

        <Switch
          control={control}
          disabled={isDisabled}
          label={t('enabled.label')}
          labelPlacement='start'
          name='enabled'
        />
      </StackForm>
    </BoxForm>
  )
}
